import React from 'react';
import { error } from './alert';

/**
 * Current URL.
 *
 * @type {URL}
 */
export const currentUrl = new URL(window.location);

// noinspection JSUnresolvedVariable
/**
 * Company base URL eq. "https://domain/Companies/1999"
 *
 * @type {string}
 */
export const companyBaseUrl = `${currentUrl.origin}/Companies/${globalParameters.companyId}`;

/**
 * Wrapper for "fetch" that handles redirects properly and adds the base URL.
 *
 * @param uri
 * @param init
 * @returns {Promise<Response>}
 */
export const request = (uri = '', init = {}) => {
    const url = `${companyBaseUrl}${uri}`;

    init.mode = 'same-origin';

    if (!init.method) {
        init.method = 'GET';
    }

    if (!init.headers && init.method === 'GET') {
        init.headers = { 'Content-Type': 'application/json' };
    }

    return new Promise(async (resolve, reject) => {
        const res = await fetch(url, init);

        if (res.redirected) {
            window.location.href = res.url;
            reject('redirected');
        }

        resolve(res);
    })
}

/**
 * Redirect to given URI. Company base URL is prefixed.
 *
 * @param {string} uri
 */
export const redirect = (uri = '') => {
    const returnUrl = `${companyBaseUrl}${uri}`;

    return window.location = returnUrl;
}

/**
 * Get full URL for given resource.
 *
 * @param {string} uri
 * @returns {string}
 */
export const getUrl = (uri = '') => {
    return `${companyBaseUrl}${uri}`;
}

/**
 * @param {Response} response
 * @returns {Promise<Object>}
 */
export const toJson = (response) => {
    return new Promise(resolve => {
        if (response.headers?.get('content-type')?.includes('application/json')) {
            resolve(response.json());
        } else {
            resolve({});
        }
    });
}

/**
* @param {Response} response
* @param {Object} parameters
* @param {string} parameters.toastErrorMessage Error message shown in a toast popup. If not assigned, no message is displayed. Default: null
* @param {string} parameters.logErrorMessage Error message printed to the log. If not assigned, no message is printed. Default: null
* @param {boolean} parameters.isBooleanCheck If true, returns true or false depending on the response status. Default: false
* @param {number[]} parameters.successStatuses List of http status codes, interpreted as successful calls. Default: [200]
* @param {boolean} parameters.showToastErrorStatusCode If true, shows status code on toast error popups. Default: false
* @param {boolean} parameters.simpleToastErrorMessage If true, simplify toast error popup messages. Default: false
* @param {string|string[]} parameters.responseErrorMessageProperty Name or path to error message(s) in the response. Target property can be string or string[]. Example: 'message' => responseData.message Example 2: ['error', 'message'] => responseData.error.message Default: null
*/
export async function parseResponse(response, parameters) {
    //Check parameters
    parameters = parameters ?? {};
    parameters.toastErrorMessage = typeof parameters.toastErrorMessage === 'string' && parameters.toastErrorMessage.length !== 0 ? parameters.toastErrorMessage : null;
    parameters.logErrorMessage = typeof parameters.logErrorMessage === 'string' && parameters.logErrorMessage.length !== 0 ? parameters.logErrorMessage : null;
    parameters.isBooleanCheck = parameters.isBooleanCheck === true;
    parameters.successStatuses = Array.isArray(parameters.successStatuses) && parameters.successStatuses.length !== 0 ? parameters.successStatuses : [200];
    parameters.responseErrorMessageProperty = (typeof parameters.responseErrorMessageProperty === 'string' && parameters.responseErrorMessageProperty.length !== 0) ||
        (Array.isArray(parameters.responseErrorMessageProperty) && parameters.responseErrorMessageProperty.length !== 0) ? parameters.responseErrorMessageProperty : null;
    parameters.showToastErrorStatusCode = parameters.showToastErrorStatusCode === true;
    parameters.simpleToastErrorMessage = parameters.simpleToastErrorMessage === true;

    //Get response data
    var data = await toJson(response);

    if (parameters.successStatuses.indexOf(response.status) !== -1) {
        //Success
        return parameters.isBooleanCheck === true ? true : data;
    }

    //Error
    if (parameters.logErrorMessage !== null) {
        //Print log
        console.log(parameters.logErrorMessage + ' Http status: ' + response.status);
    }

    if (parameters.responseErrorMessageProperty !== null) {
        //Get response error message (if one exists)
        var responseErrorMessage;
        if (typeof parameters.responseErrorMessageProperty === 'string') {
            responseErrorMessage = data[parameters.responseErrorMessageProperty];
        } else {
            responseErrorMessage = data[parameters.responseErrorMessageProperty[0]];
            if (parameters.responseErrorMessageProperty.length > 1) {
                for (var i = 1; i < parameters.responseErrorMessageProperty.length; i++) {
                    if (responseErrorMessage !== undefined && responseErrorMessage !== null) {
                        responseErrorMessage = responseErrorMessage[parameters.responseErrorMessageProperty[i]];
                    }
                }
            }
        }

        if ((typeof responseErrorMessage === 'string' && responseErrorMessage.length !== 0) || (Array.isArray(responseErrorMessage) && responseErrorMessage.length !== 0)) {
            //Error message found
            responseErrorMessage = Array.isArray(responseErrorMessage) ? responseErrorMessage : [responseErrorMessage];

            //Parse toast html
            var toastItems = [];

            if (parameters.toastErrorMessage !== null) {
                //Toast error message
                toastItems.push(parameters.toastErrorMessage);
            }

            //Response error(s)
            if (responseErrorMessage.length === 1) {
                if (parameters.simpleToastErrorMessage) {
                    toastItems.push(responseErrorMessage[0]);
                } else {
                    toastItems.push(localization.ErrorMessage2 + ': ' + responseErrorMessage[0]);
                }
            } else {
                if (!parameters.simpleToastErrorMessage) {
                    toastItems.push(localization.Errors + ':');
                }
                for (var i = 0; i < responseErrorMessage.length; i++) {
                    toastItems.push(responseErrorMessage[i]);
                }
            }

            //Status code
            if (parameters.showToastErrorStatusCode) {
                toastItems.push(localization.StatusCode + ': ' + response.status);
            }

            error({
                description:
                    <div className="ui list toast-error-message">
                        {toastItems.map((item) => {
                            return (
                                <div className="item">
                                    {item}
                                </div>
                            );
                        })}
                    </div>
            });
        }

    } else if (parameters.toastErrorMessage !== null) {
        //Toast error message
        error(parameters.toastErrorMessage);
    }

    return parameters.isBooleanCheck === true ? false : null;
}